import * as signalNames from './signalNames';
import { DATES, SOFTWARE, TEXT_LAYER_TEXT_GROUP, ANOMALY } from './metadata';
import { downloadDocument } from '../utils/download';
import * as colors from '@/constants/colors';

export const PREFERRED_SIGNAL_ORDER = [
  signalNames.XRAY,
  signalNames.XRAY_LIMITED,
  signalNames.FONTS,
  signalNames.MASKING,
  signalNames.MASKING_LIMITED,
  signalNames.SOFTWARE_BLOCKLIST,
  signalNames.EDITED_TEXT,
  signalNames.TEXT_COMPRESSION,
  signalNames.TEXT_LAYER_TEXT,
  signalNames.PDF_ALIGNMENT,
  signalNames.FINGERPRINT,
  signalNames.CUSTOM_RULES,
  signalNames.BLOCKLISTS,
  signalNames.TEMPLATES,
  signalNames.REJECTED_CUSTOMER,
  signalNames.ADOBE_FONTS,
  signalNames.EDITED_WHILE_SCANNED,
  signalNames.OVERLAID_TEXT,
  signalNames.ANNOTATION_DATES,
  signalNames.MALFORMED_DATE,
  signalNames.DATE_METADATA,
  signalNames.TOUCHUP_TEXT,
  signalNames.QPDF,
  signalNames.EXIF_DATA_MODIFIED,
  signalNames.HIGH_USER_ACCESS,
  signalNames.ANNOTATIONS,
  signalNames.FLATTENED_PDF,
  signalNames.SEEN_BEFORE,
  signalNames.BRUSH_DETECTION,
  signalNames.COPYCAT_IMAGE,
  signalNames.COPYCAT_TEXT,
  signalNames.FONT_ANOMALY,
  signalNames.METADATA_ANOMALY,
  signalNames.NAME_FORMAT_ANOMALY,
  signalNames.ADDRESS_FORMAT_ANOMALY,
  signalNames.DATE_FORMAT_ANOMALY,
  signalNames.REPEAT_ACCOUNT_NUMBER,
  signalNames.REPEAT_APPLICATION,
  signalNames.REPEAT_APPLICATION_DIFFERENT_ADDRESS,
  signalNames.REPEAT_APPLICATION_DIFFERENT_NAME,
  signalNames.REPEAT_APPLICATION_PREVIOUS_FRAUD,
  signalNames.REPEAT_APPLICATION_PREVIOUS_REJECTION,
  signalNames.REPEAT_BALANCES,
  signalNames.TEXT_ALIGNMENT,
  signalNames.HIGH_RISK_CUSTOMER_SEEN_BEFORE,
  signalNames.AI_GENERATED,
  signalNames.INCONSISTENT_EIN,
  signalNames.STRIPPED_METADATA,
  signalNames.PDF_STRUCTURE_ANOMALY,
];

const sumIssuesAcrossPages = (pages, getIssuesPerPage) =>
  pages.reduce((sum, page) => sum + getIssuesPerPage(page), 0);

const sumSuspiciousRegions = (pages) => {
  return sumIssuesAcrossPages(pages, ({ instances }) => instances.length);
};

const getPagesText = (numPages) =>
  numPages > 1 ? ` across ${numPages} page${numPages > 1 ? 's' : ''}` : '';

const getCountCustomersText = (numCustomers) =>
  numCustomers >= 1
    ? ` ${numCustomers} customer${numCustomers > 1 ? 's' : ''}`
    : '';

const get_vendor_name = (vendor) => {
  switch (vendor) {
    case 'GOOGLE_AI':
      return 'Google AI';
    case 'GROK':
      return 'Grok';
    case 'OPENAI':
      return 'OpenAI';
    case 'REVE':
      return 'Reve';
    case 'IDEOGRAM':
      return 'Ideogram';
    case 'NEURAL_LOVE':
      return 'Neural Love';
    default:
      return null;
  }
};

export default {
  [signalNames.FONTS]: {
    icon: 'font',
    title: 'Font Issues',
    getDescription: ({ pages }) => {
      const issues = sumIssuesAcrossPages(
        pages,
        ({ inconsistent_words: words }) => words.length,
      );
      return `${issues} inconsistenc${issues === 1 ? 'y' : 'ies'}${getPagesText(
        pages.length,
      )}`;
    },
    helpArticle: '3496550-fonts',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'Multiple fonts are used within the same word or section of text.',
  },
  [signalNames.MASKING]: {
    icon: 'draw-polygon',
    title: 'Masking',
    getDescription: ({ pages }) => {
      const numRegions = sumSuspiciousRegions(pages);
      return `${numRegions} masked region${
        numRegions > 1 ? 's' : ''
      }${getPagesText(pages.length)}`;
    },
    helpArticle: '3505122-masking',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'Some text in the document has been deliberately hidden or obscured.',
  },
  [signalNames.MASKING_LIMITED]: {
    icon: 'draw-polygon',
    title: 'Masking Key Details',
    getDescription: ({ pages }) => {
      const numRegions = sumSuspiciousRegions(pages);
      return `${numRegions} masked region${
        numRegions > 1 ? 's' : ''
      }${getPagesText(pages.length)}`;
    },
    helpArticle: '3505122-masking',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'Identity key details in the document have been deliberately hidden or obscured.',
  },
  [signalNames.TEXT_COMPRESSION]: {
    icon: 'text-width',
    title: 'Text Compression',
    getDescription: ({ pages }) => {
      const numRegions = sumSuspiciousRegions(pages);
      return `${numRegions} suspicious region${
        numRegions > 1 ? 's' : ''
      }${getPagesText(pages.length)}`;
    },
    helpArticle: '3672332-text-compression',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
  },
  [signalNames.BRUSH_DETECTION]: {
    icon: 'paint-roller',
    title: 'Brush Detection',
    getDescription: ({ pages }) => {
      const numRegions = sumSuspiciousRegions(pages);
      return `${numRegions} suspicious region${
        numRegions > 1 ? 's' : ''
      }${getPagesText(pages.length)}`;
    },
    helpArticle: '5291307-brush-detection',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'A photo editor has been used to cover and replace text in the document.',
  },
  [signalNames.PDF_ALIGNMENT]: {
    icon: 'align-left',
    title: 'PDF Alignment',
    getDescription: ({ pages }) => {
      let description = '';
      if (pages.length === 1) {
        description = pages[0].description;
      } else if (pages.length > 1) {
        const numRegions = sumSuspiciousRegions(pages);
        description = `${numRegions} suspicious region${
          numRegions > 1 ? 's' : ''
        }${getPagesText(pages.length)}`;
      }
      return description;
    },
    helpArticle: '5402701-pdf-alignment',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'Text like name or address is misaligned within the document layout.',
  },
  [signalNames.OVERLAID_TEXT]: {
    icon: 'layer-group',
    title: 'Overlaid Text',
    helpArticle: '3505126-overlaid-text',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'Text has been digitally placed over an image in the document.',
  },
  [signalNames.EDITED_TEXT]: {
    icon: 'pen-to-square',
    title: 'Edited Text',
    getDescription: ({ pages }) => {
      const numRegions = sumSuspiciousRegions(pages);
      return `${numRegions} suspicious region${
        numRegions > 1 ? 's' : ''
      }${getPagesText(pages.length)}`;
    },
    helpArticle: '3557476-edited-text',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'Some text in the document has been edited, though the exact change may be hard to pinpoint.',
  },
  [signalNames.FINGERPRINT]: {
    icon: 'fingerprint',
    title: 'Fingerprint',
    helpArticle: '4377726-fingerprinting',
    getDescription: ({ institution }) => {
      return `${institution.name} fingerprint mismatch`;
    },
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'The document doesn’t match the expected design or structure of the claimed institution.',
  },
  [signalNames.DATE_METADATA]: {
    icon: 'calendar',
    title: 'Dates',
    relatedMetadata: DATES,
    helpArticle: '3505132-metadata',
    tooltipText: 'The file was edited after its original creation date.',
  },
  [signalNames.QPDF]: {
    icon: 'list',
    title: 'Edited with QPDF',
  },
  [signalNames.EXIF_DATA_MODIFIED]: {
    icon: 'list',
    title: 'Exif Issue',
    getDescription: () => 'Metadata edited with exif tool',
    tooltipText:
      'Metadata in the file has likely been wiped or modified to hide signs of fraud.',
  },
  [signalNames.EDITED_WHILE_SCANNED]: {
    icon: 'marker',
    title: 'Edit After Scan',
    getDescription: () => 'Document edited after scanning',
    helpArticle: '3505132-metadata',
    tooltipText: 'The document was modified after being scanned.',
  },
  [signalNames.HIGH_USER_ACCESS]: {
    icon: 'shield-halved',
    title: 'Editing Allowed',
  },
  [signalNames.ADOBE_FONTS]: {
    icon: 'text',
    title: 'Adobe Fonts',
    getDescription: () => 'Text edited using Adobe Fonts',
    tooltipText: 'The document was likely edited using Adobe software.',
  },
  [signalNames.ANNOTATION_DATES]: {
    icon: 'calendar',
    title: 'Annotation Dates',
    helpArticle: '3505132-metadata',
    tooltipText:
      'Annotations suggest the document was edited after its creation date.',
  },
  [signalNames.ANNOTATIONS]: {
    icon: 'message',
    title: 'Annotations',
    helpArticle: '9088193-annotations',
  },
  [signalNames.MALFORMED_DATE]: {
    icon: 'calendar',
    title: 'Malformed Date',
    helpArticle: '3505132-metadata',
    tooltipText:
      'The metadata includes date formats that are corrupted or invalid.',
  },
  [signalNames.TEXT_LAYER_TEXT]: {
    icon: 'text',
    title: 'Photoshop',
    helpArticle: '3505132-metadata',
    relatedMetadata: TEXT_LAYER_TEXT_GROUP,
    tooltipText: 'Text has been added to the document using Photoshop.',
  },
  [signalNames.FLATTENED_PDF]: {
    icon: 'weight-hanging',
    title: 'Flattened PDF',
    helpArticle: '4680342-flattened-pdf',
    tooltipText: 'A normally True PDF has been converted to an image format.',
  },
  [signalNames.IS_NOT_BALANCED]: {
    title: 'Transactions Do Not Balance',
    helpArticle: '9317949-transactions-do-not-balance',
  },
  [signalNames.TOUCHUP_TEXT]: {
    icon: 'pen-to-square',
    title: 'Touch-up text',
  },
  [signalNames.CUSTOM_RULES]: {
    icon: 'bolt',
    title: 'Custom Rules',
    getDescription: (signal) => {
      const triggeredRules = Object.keys(signal).filter((key) => !isNaN(key));
      const num = triggeredRules.length;
      return triggeredRules.length
        ? `${num} custom rule${num > 1 ? 's' : ''} triggered`
        : '';
    },
  },
  [signalNames.SOFTWARE_BLOCKLIST]: {
    icon: 'list',
    title: 'Software',
    getDescription: ({ description }) => description || 'No software issues',
    relatedMetadata: SOFTWARE,
    helpArticle: '3505132-metadata',
    tooltipText:
      'The document was created or modified using common consumer editing tools.',
  },
  [signalNames.SEEN_BEFORE]: {
    icon: 'clock-rotate-left',
    title: 'Seen Before',
    getDescription: ({ documents }) =>
      `Document has been submitted before${
        documents && documents.length > 1 ? ` ${documents.length} times` : ''
      }`,
    helpArticle: '3505127-seen-before',
    tooltipText:
      'This exact document has been processed previously in your system.',
  },
  [signalNames.XRAY]: {
    icon: 'window-restore',
    title: 'X-ray',
    ctaHandler: ({ recovered_version }) =>
      downloadDocument(recovered_version.url, 'recovered-version.pdf'),
    getCtaText: () => 'Download',
    getDescription: () => 'Previous version has been recovered',
    helpArticle: '3505129-x-ray-previous-version-recovered',
    tooltipText:
      'A previous version of the document reveals changes or tampering.',
  },
  [signalNames.XRAY_LIMITED]: {
    icon: 'window-restore',
    title: 'X-ray Key Details',
    ctaHandler: ({ recovered_version }) =>
      downloadDocument(recovered_version.url, 'recovered-version.pdf'),
    getCtaText: () => 'Download',
    getDescription: () => 'Previous version has been recovered',
    helpArticle: '3505129-x-ray-previous-version-recovered',
    tooltipText:
      'A previous version of the document reveals changes or tampering over identity key details.',
  },
  [signalNames.BLOCKLISTS]: {
    icon: 'user-xmark',
    title: 'Blocklists',
    getDescription: ({ pages }) => {
      const firstPage = pages[0];
      const type = firstPage?.type || 'property';
      const description = firstPage?.description;

      const displayText = description ? ` '${description}'` : '';

      return `Blocklisted ${type.toLowerCase()}${displayText}${
        pages.length > 1 ? ` and ${pages.length - 1} more` : ''
      } found`;
    },
    helpArticle: '3650412-blacklist-database',
    tooltipText:
      'The document includes a name, address, or phone number found on your blocklist.',
  },
  [signalNames.REJECTED_CUSTOMER]: {
    icon: 'person-circle-exclamation',
    title: 'Rejected Customer',
    getDescription: () =>
      `Customer with this address has been previously rejected`,
    helpArticle: '3959143-parsing',
  },
  [signalNames.TEMPLATES]: {
    icon: 'images',
    title: 'Templates',
    getDescription: () => 'This document matches a known template',
    getCtaText: () => 'View',
    helpArticle: '3650410-template-database',
    tooltipText:
      'The document matches a known fake template from document creation websites.',
  },
  [signalNames.NAME]: {
    icon: 'user',
    title: 'Name',
    getTitle: ({ failed }) =>
      failed ? 'Name verification failed' : 'Name successfully verified',
  },
  [signalNames.ADDRESS]: {
    icon: 'house',
    title: 'Address',
    getTitle: ({ failed }) =>
      failed ? 'Address verification failed' : 'Address successfully verified',
  },
  [signalNames.COMPANY]: {
    icon: 'building',
    title: 'Company',
    getTitle: ({ failed }) =>
      failed ? 'Company verification failed' : 'Company successfully verified',
  },
  [signalNames.ID_NUMBER]: {
    icon: 'hashtag',
    title: 'ID Number',
    getTitle: ({ failed }) =>
      failed
        ? 'ID number verification failed'
        : 'ID number successfully verified',
  },
  [signalNames.STRINGS]: {
    icon: 'text',
    title: 'Strings',
    getTitle: (signal) => {
      if (signal.key) {
        return signal.failed
          ? `${signal.key} verification failed`
          : `${signal.key} successfully verified`;
      } else {
        return signal.failed
          ? 'String verification failed'
          : 'String successfully verified';
      }
    },
  },
  [signalNames.COPYCAT_IMAGE]: {
    icon: 'camera',
    title: 'Copycat Image',
    getDescription: () => 'Image seen before under a different identity',
    helpArticle: '5908765-copycat-image',
    tooltipText:
      'The document image closely matches other documents tied to different identities.',
  },
  [signalNames.COPYCAT_TEXT]: {
    icon: 'keyboard',
    title: 'Copycat Text',
    getDescription: () =>
      'Document contents seen before under a different identity',
    helpArticle: '6015469-copycat-text',
    tooltipText:
      'The document text closely matches other documents tied to different identities.',
  },
  [signalNames.REPEAT_ACCOUNT_NUMBER]: {
    title: 'Repeat Account Number',
    getDescription: ({ documents }) =>
      `Seen ${
        documents && documents.length > 1 ? ` ${documents.length} times` : ''
      } before under a different identity`,
    tooltipText:
      'The same account number appears in documents for different identities.',
  },
  [signalNames.FONT_ANOMALY]: {
    icon: 'link-slash',
    title: 'Font Anomaly',
    relatedMetadata: ANOMALY,
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    helpArticle: '6084222-anomaly-detection',
    tooltipText:
      'Fonts used in the document are inconsistent with those typically used by the institution.',
  },
  [signalNames.METADATA_ANOMALY]: {
    icon: 'triangle-exclamation',
    title: 'Metadata Anomaly',
    relatedMetadata: ANOMALY,
    helpArticle: '6084222-anomaly-detection',
    tooltipText:
      'Metadata values differ from what’s typical for the document’s type or source.',
  },
  [signalNames.NAME_FORMAT_ANOMALY]: {
    icon: 'user',
    title: 'Name Format Anomaly',
    getDescription: () => 'Name case does not match similar documents',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    helpArticle: '6084222-anomaly-detection',
    tooltipText:
      'The name format (uppercase, lowercase, etc.) is different from standard formats.',
  },
  [signalNames.ADDRESS_FORMAT_ANOMALY]: {
    icon: 'building',
    title: 'Address Format Anomaly',
    getDescription: () => 'Address case does not match similar documents',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    helpArticle: '6084222-anomaly-detection',
    tooltipText:
      "The document's address formatting differs from standard formats.",
  },
  [signalNames.DATE_FORMAT_ANOMALY]: {
    icon: 'calendar',
    title: 'Date Format Anomaly',
    getDescription: () => 'Date format does not match similar documents',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    helpArticle: '6084222-anomaly-detection',
    tooltipText:
      'Date formatting differs from what is typical for similar documents.',
  },
  [signalNames.AMOUNT_FORMAT_ANOMALY]: {
    icon: 'apostrophe',
    title: 'Amount Format Anomaly',
    getDescription: () => 'Amount format does not match similar documents',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    helpArticle: '6084222-anomaly-detection',
    tooltipText:
      'The amount formatting (e.g., commas, currency symbols) is unusual.',
  },
  [signalNames.REPEAT_APPLICATION]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application',
    getDescription: () =>
      'User device previously associated with another application',
  },
  [signalNames.REPEAT_APPLICATION_DIFFERENT_ADDRESS]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application with Different Address',
    getDescription: () =>
      'User device previously associated with a different address',
  },
  [signalNames.REPEAT_APPLICATION_DIFFERENT_NAME]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application with Different Name',
    getDescription: () =>
      'User device previously associated with a different name',
  },
  [signalNames.REPEAT_APPLICATION_PREVIOUS_FRAUD]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application with Previous Fraud',
    getDescription: () =>
      'User device previously associated with fraudulent documents',
  },
  [signalNames.REPEAT_APPLICATION_PREVIOUS_REJECTION]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application with Previous Rejection',
    getDescription: () =>
      'User device previously associated with a rejected Customer',
  },
  [signalNames.REPEAT_BALANCES]: {
    title: 'Repeat Balances',
    getDescription: ({ documents }) =>
      `Balance amounts seen ${
        documents && documents.length > 1 ? ` ${documents.length} times` : ''
      } before under a different identity`,
    tooltipText:
      'The statement uses repeated balances or transactions seen in other identities.',
  },
  [signalNames.TEXT_ALIGNMENT]: {
    icon: 'keyboard',
    title: 'Text Alignment',
    getDescription: () => 'Text alignment inconsistencies found',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText: 'Key text elements are misaligned on the page.',
  },
  [signalNames.SUMMARY_NOT_BALANCED]: {
    title: 'Summary Section Does Not Balance',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'Financial totals in the summary section don’t add up correctly.',
  },
  [signalNames.GENERIC_DATA]: {
    title: 'Generic Data',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'The document contains placeholder or generic info like "John Doe" or "123 Anywhere St".',
  },
  [signalNames.PAYSLIP_MISCALCULATION]: {
    title: 'Payslip Miscalculation',
    getDescription: () => 'Miscalculation detected in the payslip',
    color: colors.FRAUD_SIGNAL_BOUNDING_BOX_COLOR_RGBA,
    tooltipText:
      'The deductions section on the payslip has incorrect or inconsistent totals.',
  },
  [signalNames.HIGH_RISK_CUSTOMER_SEEN_BEFORE]: {
    title: 'High Risk Customer Seen Before',
    getDescription: ({ customers }) =>
      `Document has been submitted before for${
        customers
          ? ` ${getCountCustomersText(customers.length)} with a high risk document`
          : ''
      }`,
    tooltipText:
      'A previously uploaded document matches this one and is linked to a high-risk applicant.',
  },
  // Transaction signals
  [signalNames.BANK_CHARGE_IDENTIFIED]: {
    title: 'Bank Charges Identified',
    description: 'Unusual bank fees or charges detected',
  },
  [signalNames.CRYPTO_TRADING_IDENTIFIED]: {
    title: 'Crypto Identified',
    description: 'Cryptocurrency transaction detected',
  },
  [signalNames.GAMBLING_IDENTIFIED]: {
    title: 'Gambling Identified',
    description: 'Gambling-related transaction detected',
  },
  [signalNames.BENFORDS_LAW_VIOLATION]: {
    title: "Benford's Law Violation",
    description: 'Transaction pattern deviates from expected distribution',
  },
  [signalNames.LOANS_IDENTIFIED]: {
    title: 'Loan Identified',
    description: 'Transaction related to loan disbursement detected',
  },
  [signalNames.UNUSUAL_AVERAGE_DAILY_BALANCE]: {
    title: 'Unusually High Average Daily Balance',
    description: 'Suspicious variation in daily balance',
  },
  [signalNames.UNUSUAL_ROUND_NUMBER_DISTRIBUTION]: {
    title: 'Unusually High Round Number Distribution',
    description: 'High frequency of rounded amounts detected',
  },
  [signalNames.AI_GENERATED]: {
    title: 'AI Generated',
    description: 'This document was created using AI',
    getDescription: ({ ai_vendor }) => {
      const name = get_vendor_name(ai_vendor);
      return name
        ? `This document was created using AI tools from ${name}`
        : 'This document was created using AI tools';
    },
  },
  [signalNames.INCONSISTENT_EIN]: {
    title: 'Inconsistent EIN',
    description: 'Multiple EINs found in the document',
    getDescription: ({ eins }) => {
      return `EINs detected: ${eins.join(', ')}`;
    },
  },
  [signalNames.STRIPPED_METADATA]: {
    title: 'Stripped Metadata',
    description: 'Document has unexpected empty fields in metadata',
  },
  [signalNames.PDF_STRUCTURE_ANOMALY]: {
    title: 'PDF Structure Anomaly',
    description: 'Internal structure (PDF stream) shows anomalies',
  },
};
